// Dependencies
import React, { FC, useContext, useEffect, useState } from 'react';
import { Link, useLocation, useMatch } from 'react-router-dom';
import {
  Box,
  Button,
  CircularProgress,
  Divider,
  Drawer,
  Fab, FormControlLabel,
  IconButton,
  InputAdornment,
  OutlinedInput,
  Stack, Switch,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';

// Context
import { AppContext } from '../../../context';
import { useToastContext } from '../../../components/Toast/toastContext';

// Resource
import { DisplayMode, ORDER } from '../../../resource';

// Apis
import apis from '../../../apis';

// Constants
import { PAGE_NAME, ROUTES, getPageName } from '../../../contants';

// Icons
import { DeleteIcon, ListIcon, MenuIcon, ModuleIcon, PencilIcon, PlusIcon, CloseIcon } from '../../../assets/icons';

// Styles
import styles from './styles';
import Sidebar from '../Sidebar';

// Export header
const Header: FC = () => {
  // States
  const [openSearch, setOpenSearch] = useState<boolean>(false);
  const [openDrawer, setOpenDrawer] = useState<boolean>(false);
  const [isSearchFocused, setIsSearchFocused] = useState<boolean>(false);

  // Check dashboard page
  const isDashboard = Boolean(useMatch(ROUTES.DASHBOARD));
  const isUsersPage = Boolean(useMatch(ROUTES.USER.LIST));
  const isCustomerDocumentsPage = Boolean(useMatch(ROUTES.CUSTOMER_DOCUMENT));
  const isPdfPage = Boolean(useMatch(ROUTES.PDF_VIEWER))
  const isCdnPage = Boolean(useMatch(ROUTES.MANAGE_WIDGETS));
  const isCdnWidgetDetailPage = Boolean(useMatch(ROUTES.WIDGET_EDIT));
  const isPdfConfigPage = Boolean(useMatch(ROUTES.PDF_VIEWER_V2));
  const isPortfolioUploadPage = Boolean(useMatch(ROUTES.PORTFOLIO_UPLOAD));

  useEffect(() => {
    setSearch("");
  }, [
    isDashboard,
    isUsersPage,
    isCustomerDocumentsPage,
    isPdfPage,
  ]);

  // Theme
  const theme = useTheme();

  // Check window size
  const isTablet = useMediaQuery(theme.breakpoints.up('md'));

  // Get context
  const {
    account,
    displayMode,
    setDisplayMode,
    dialogs,
    user,
    users,
    selectedUsers,
    customerDoc,
    customerDocs,
    esgDoc,
    esgDocs,
    showDeleted,
    setShowDeleted,
    showBackups,
    setShowBackups,
    search,
    setSearch,
    isAdmin,
  } = useContext(AppContext);

  // Get toast from hook
  const { activeToast } = useToastContext();

  // Toggle display mode handler
  const handleToggleDisplayMode = () => {
    setDisplayMode(displayMode === DisplayMode.List ? DisplayMode.Module : DisplayMode.List);
  };

  // Open new dialog handler
  const handleOpenNewDocumentDialog = () => {
    dialogs.newDoc.setVisible(true);
  };

  // Create handler
  const handleCreate = () => {
    if (isUsersPage) {
      user.setValue(undefined);
      dialogs.user.setVisible(true);
    }

    if (isCustomerDocumentsPage) {
      customerDoc.setValue(undefined);
      dialogs.customerDoc.setVisible(true);
    }
  };

  // Edit handler
  const handleEdit = () => {
    if (isUsersPage) {
      if (selectedUsers.value.length > 0) {
        apis.user
          .getUser(selectedUsers.value[0])
          .then((res) => {
            user.setValue(res);
            dialogs.user.setVisible(true);
          })
          .catch((err) => {
            console.log(err);
          });
      } else {
        activeToast({
          type: 'warning',
          text: 'No selected user!',
        });
      }
    }

    if (isCustomerDocumentsPage && account) {
      if (customerDocs.selected.length > 0) {
        apis.pdf
          .getCustomerDocument(account.id, customerDocs.selected[0])
          .then((res) => {
            customerDoc.setValue(res.document);
            dialogs.customerDoc.setVisible(true);
          })
          .catch((err) => {
            console.log(err);
          });
      } else {
        activeToast({
          type: 'warning',
          text: 'No selected document!',
        });
      }
    }
  };

  // Delete handler
  const handleDelete = () => {
    if (isUsersPage) {
      if (selectedUsers.value.length > 0) {
        apis.user
          .removeUsers(selectedUsers.value)
          .then((res) => {
            activeToast({
              type: 'success',
              text: res.msg,
            });
            users.fetchData({
              options: {
                limit: 15,
                skip: 0,
                sort: {
                  firstName: ORDER.ASC,
                },
              },
            });
          })
          .catch((err) => {
            activeToast({
              type: 'error',
              text: err.msg,
            });
          })
          .finally(() => {
            customerDoc.setValue(undefined);
            selectedUsers.setValue([]);
          });
      } else {
        activeToast({
          type: 'warning',
          text: 'No selected user!',
        });
      }
    }

    if (isCustomerDocumentsPage && account) {
      if (customerDocs.selected.length > 0) {
        apis.pdf
          .removeCustomerDocuments(account.id, customerDocs.selected)
          .then((res) => {
            activeToast({
              type: 'success',
              text: res.msg,
            });
            customerDocs.fetchData(account.id);
          })
          .catch((err) => {
            activeToast({
              type: 'error',
              text: err.msg,
            });
          })
          .finally(() => {
            customerDocs.setSelected([]);
          });
      } else {
        activeToast({
          type: 'warning',
          text: 'No selected document!',
        });
      }
    }
  };

  // Get location from hook
  const { pathname } = useLocation();

  const handleSearch = (e) => {
    setSearch(e.target.value);
    // Keep search expanded when there's content
    if (e.target.value) {
      setOpenSearch(true);
    }
  };

  // Clear search handler
  const handleClearSearch = () => {
    setSearch("");
    setOpenSearch(false);
  };

  // Open search panel handler
  const handleOpenSearchPanel = () => {
    setOpenSearch(true);
  };

  // Close search panel handler
  const handleCloseSearchPanel = () => {
    // Only close if no content and not focused
    if (!search && !isSearchFocused) {
      setOpenSearch(false);
    }
  };

  // Focus handlers
  const handleSearchFocus = () => {
    setIsSearchFocused(true);
    setOpenSearch(true);
  };

  const handleSearchBlur = () => {
    setIsSearchFocused(false);
    // Keep expanded if there's content
    if (!search) {
      setOpenSearch(false);
    }
  };

  // Auto-expand/collapse search bar based on search value
  useEffect(() => {
    if (search && search.trim() !== '') {
      setOpenSearch(true);
    } else {
      // Only collapse if not focused
      if (!isSearchFocused) {
        setOpenSearch(false);
      }
    }
  }, [search, isSearchFocused]);

  // Keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Ctrl+K to focus search
      if ((e.ctrlKey || e.metaKey) && e.key === 'k') {
        e.preventDefault();
        const searchInput = document.querySelector('input[placeholder="Search"]') as HTMLInputElement;
        if (searchInput) {
          searchInput.focus();
        }
      }
      // Escape to clear search
      if (e.key === 'Escape' && search) {
        handleClearSearch();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [search]);

  // Open drawer handler
  const handleOpenDrawer = () => {
    setOpenDrawer(true);
  };

  // Close drawer handler
  const handleCloseDrawer = () => {
    setOpenDrawer(false);
  };

  // On mounted
  useEffect(() => {
    if (isTablet) {
      setOpenDrawer(false);
    }
  }, [isTablet]);

  // Check module pages
  const isModuleWithArchive = isDashboard || isUsersPage || isCustomerDocumentsPage || isCdnPage || isPortfolioUploadPage;

  // Return header
  return (
    <>
      <Stack sx={styles.header}>
        <Stack direction="row" alignItems="center" justifyContent="space-between">
          <Box sx={{ height: { xs: 52, md: 46 } }}>
            <Box sx={{ display: { xs: 'none', md: 'flex' }, alignItems: 'center' }}>
              {isDashboard && (
                <Box sx={styles.newButton} onClick={handleOpenNewDocumentDialog}>
                  <Fab>
                    <img src="/icons/plus.svg" alt="plus" />
                  </Fab>
                  <Button variant="outlined">New</Button>
                </Box>
              )}
              <Typography variant="headline">{getPageName(pathname)}</Typography>
              {isModuleWithArchive && (
                <FormControlLabel
                  sx={{ ml: 12 }}
                  onChange={() => {
                    console.log('Archived toggle clicked, current showDeleted:', showDeleted);
                    setShowDeleted(!showDeleted);
                  }}
                  control={<Switch checked={showDeleted} color="primary" />}
                  label="Archived"
                />
              )}
              {isDashboard && (
                <FormControlLabel
                  sx={{ ml: 12 }}
                  onChange={() => setShowBackups(!showBackups)}
                  control={<Switch checked={showBackups} color="primary" />}
                  label="Backups"
                />
              )}
            </Box>
            {!openSearch && (
              <Box sx={{ display: { xs: 'flex', md: 'none' }, alignItems: 'center' }}>
                {/* @ts-ignore */}
                <IconButton size="medium" variant="text" onClick={handleOpenDrawer}>
                  <MenuIcon />
                </IconButton>
                <Box sx={styles.logo} component={Link} to={ROUTES.DASHBOARD}>
                  <img src="/logo.png" alt="logo" />
                </Box>
              </Box>
            )}
          </Box>

          {!isPdfPage && !isCdnPage && !isCdnWidgetDetailPage && !isPdfConfigPage && !isPortfolioUploadPage && (
            <Stack
              direction="row"
              spacing={{ xs: 8, md: 16 }}
              divider={(isUsersPage || isCustomerDocumentsPage) && <Divider orientation="vertical" flexItem />}
            >
              <Tooltip 
                title="Search (Esc to clear)" 
                placement="bottom"
                arrow
              >
                <OutlinedInput
                  color="secondary"
                  sx={styles.searchInput}
                  className={openSearch ? 'expanded' : ''}
                  placeholder="Search"
                  value={search}
                  onChange={handleSearch}
                  onFocus={handleSearchFocus}
                  onBlur={handleSearchBlur}
                  onMouseEnter={handleOpenSearchPanel}
                  onMouseLeave={handleCloseSearchPanel}
                  startAdornment={
                    <InputAdornment position="start">
                      <img src="/icons/search.svg" alt="search" />
                    </InputAdornment>
                  }
                  endAdornment={
                    search && (
                      <InputAdornment position="end">
                        <IconButton
                          size="small"
                          onClick={handleClearSearch}
                          sx={{ 
                            color: 'secondary.main',
                            '&:hover': { 
                              backgroundColor: 'rgba(0, 0, 0, 0.04)' 
                            }
                          }}
                        >
                          <CloseIcon />
                        </IconButton>
                      </InputAdornment>
                    )
                  }
                />
              </Tooltip>
              {(isUsersPage || isCustomerDocumentsPage) && (
                <Stack direction="row" spacing={16}>
                  <IconButton size="medium" onClick={handleDelete}>
                    <DeleteIcon />
                  </IconButton>
                  <IconButton size="medium" onClick={handleEdit}>
                    <PencilIcon />
                  </IconButton>
                </Stack>
              )}
              {(isUsersPage || isCustomerDocumentsPage) && (
                <Button 
                  disabled={isUsersPage ? !isAdmin : !esgDocs.esgDocAllowed} 
                  startIcon={<PlusIcon />} 
                  variant="varied-outlined" 
                  onClick={handleCreate}
                >
                  Add New{' '}
                  {isTablet && (
                    <>
                      {isUsersPage && 'User'}
                      {(isCustomerDocumentsPage) && 'Document'}
                    </>
                  )}
                </Button>
              )}
              {isDashboard && (
                <Button
                  sx={styles.toggleButton}
                  variant={displayMode === DisplayMode.List ? 'contained' : 'outlined'}
                  onClick={handleToggleDisplayMode}
                >
                  {displayMode === DisplayMode.List ? <ListIcon /> : <ModuleIcon />}
                </Button>
              )}
            </Stack>
          )}
        </Stack>
        <Stack direction="row" alignItems="center" sx={{ display: { xs: 'flex', md: 'none' } }}>
          {isDashboard && (
            <Box sx={styles.newButton} onClick={handleOpenNewDocumentDialog}>
              <Fab>
                <img src="/icons/plus.svg" alt="plus" />
              </Fab>
              <Button variant="outlined">New</Button>
            </Box>
          )}
          <Typography variant="headline">{PAGE_NAME[pathname]}</Typography>
        </Stack>
      </Stack>
      <Drawer anchor="left" open={openDrawer} onClose={handleCloseDrawer} sx={styles.drawer}>
        <Sidebar />
      </Drawer>
    </>
  );
};

// Export header
export default Header;
